import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest } from '../authConfig';
import { ROLE_TOKEN_MAP } from '../constants/roles';

/**
 * Decode JWT payload to extract claims.
 */
function decodeJwtPayload(token) {
    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return {};
    }
}

/**
 * Custom hook to resolve the current user's role from the MSAL access token.
 *
 * Returns:
 *   { role, userName, userEmail, loading, error }
 *
 * The role is determined by the `roles` claim in the access token,
 * mapped via ROLE_TOKEN_MAP to the app's Role enum.
 */
export function useUserRole() {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userName =
        activeAccount?.idTokenClaims?.name ||
        activeAccount?.name ||
        'User';

    const userEmail =
        activeAccount?.idTokenClaims?.preferred_username ||
        activeAccount?.idTokenClaims?.email ||
        activeAccount?.username ||
        '';

    useEffect(() => {
        if (!activeAccount) {
            setLoading(false);
            return;
        }

        instance
            .acquireTokenSilent({
                ...apiTokenRequest,
                account: activeAccount,
            })
            .then((response) => {
                const payload = decodeJwtPayload(response.accessToken);
                const tokenRoles = payload.roles || [];

                // Map token role strings to our Role enum
                let resolved = null;
                for (const tokenRole of tokenRoles) {
                    if (ROLE_TOKEN_MAP[tokenRole]) {
                        resolved = ROLE_TOKEN_MAP[tokenRole];
                        break;
                    }
                }

                // No fallback: users without an Enterprise App role must not enter the app as Advisor
                setRole(resolved);
                setLoading(false);
            })
            .catch((err) => {
                console.warn('useUserRole: token acquisition failed', err.message);
                setError(err);
                setRole(null);
                setLoading(false);
            });
    }, [activeAccount, instance]);

    return { role, userName, userEmail, loading, error };
}
